import { geoInterpolate, geoPath } from 'd3-geo';
import { select } from 'd3-selection';

import { type HistoryStop, historyStops } from '../data/history-stops';
import { getMotionTier, isMotionReduce } from './motion';
import {
  createGlobeProjection,
  easeInOutCubic,
  enableAxisDrag,
  fitGlobeProjection,
  GLOBE_COLORS,
  type GlobeGroup,
  type GlobeProjection,
  type GlobeSvg,
  renderOrthographicGlobe,
  setAxisRotation,
} from './orthographic-globe';

const SEGMENT_MS = 2000;
/** How far the camera drifts from segment midpoint toward destination (0–1). */
const CAMERA_DRIFT = 0.35;
const MARKER_R = 4;
const MARKER_R_ACTIVE = 9;
const MARKER_RING_WIDTH = 4;
const MARKER_BG_URL = '/themes/custom/geneva_ai_summit/public/images/bg.png';
const LABEL_OFFSET_X = MARKER_R_ACTIVE + MARKER_RING_WIDTH;
const LABEL_OFFSET_Y = 4;

const FINAL_STOP_ID = historyStops[historyStops.length - 1].id;

/** Tailwind utilities applied to the Geneva ping circle (SVG). */
const GENEVA_PING_CLASS = 'animate-ping fill-magenta opacity-75 origin-center';

const arcGradientId = (segmentId: string): string =>
  `history-arc-gradient-${segmentId}`;

const COLORS = {
  ...GLOBE_COLORS,
  white: '#ffffff',
  navyMuted: 'rgba(0, 0, 102, 0.35)',
  navyBorder: 'rgba(0, 0, 102, 0.1)',
  navyRing: 'rgba(0, 0, 102, 0.06)',
} as const;

type ArcSegment = {
  id: string;
  coordinates: [number, number][];
  dimmed: boolean;
};

const interpolateLng = (fromLng: number, toLng: number, t: number): number =>
  geoInterpolate([fromLng, 0], [toLng, 0])(t)[0];

const segmentCameraEndLng = (from: HistoryStop, to: HistoryStop): number => {
  const midpoint = geoInterpolate([from.lng, from.lat], [to.lng, to.lat])(0.5);
  return geoInterpolate(midpoint, [to.lng, to.lat])(CAMERA_DRIFT)[0];
};

const buildSegmentCameraBounds = (): { startLng: number; endLng: number }[] =>
  historyStops.slice(0, -1).map((from, index) => {
    const to = historyStops[index + 1];
    const endLng = segmentCameraEndLng(from, to);
    const startLng =
      index === 0
        ? geoInterpolate([from.lng, from.lat], [to.lng, to.lat])(0.5)[0]
        : segmentCameraEndLng(historyStops[index - 1], from);

    return { startLng, endLng };
  });

const segmentCameraBounds = buildSegmentCameraBounds();

const buildArcCoordinates = (
  from: HistoryStop,
  to: HistoryStop,
  progress = 1,
): [number, number][] => {
  const interpolate = geoInterpolate([from.lng, from.lat], [to.lng, to.lat]);
  const steps = Math.max(2, Math.ceil(progress * 100));
  const coords: [number, number][] = [];

  for (let i = 0; i <= steps; i++) {
    const t = (i / steps) * progress;
    coords.push(interpolate(t));
  }

  return coords;
};

class HistoryMapController {
  private readonly container: HTMLElement;
  private svg!: GlobeSvg;
  private globeGroup!: GlobeGroup;
  private arcsGroup!: ReturnType<typeof select<SVGGElement, unknown>>;
  private markersGroup!: ReturnType<typeof select<SVGGElement, unknown>>;
  private labelsGroup!: ReturnType<typeof select<SVGGElement, unknown>>;

  private width = 0;
  private height = 0;
  private animationFrame = 0;
  private started = false;
  private dragEnabled = false;

  private readonly projection: GlobeProjection = createGlobeProjection();
  private readonly segments: ArcSegment[] = historyStops
    .slice(0, -1)
    .map((from, index) => {
      const to = historyStops[index + 1];
      return {
        id: `${from.id}-${to.id}`,
        coordinates: [],
        dimmed: false,
      };
    });

  constructor(container: HTMLElement) {
    this.container = container;
    this.mountSvg();
    this.globeGroup = this.svg.select<SVGGElement>('[data-globe]');
    this.arcsGroup = this.svg.select<SVGGElement>('[data-arcs]');
    this.markersGroup = this.svg.select<SVGGElement>('[data-markers]');
    this.labelsGroup = this.svg.select<SVGGElement>('[data-labels]');
  }

  init(): void {
    setAxisRotation(this.projection, segmentCameraBounds[0].startLng);
    this.renderMarkers();
    this.renderLabels();
    this.observeVisibility();
    this.observeResize();
  }

  private mountSvg(): void {
    const svg = select(this.container)
      .append('svg')
      .attr('class', 'size-full touch-none')
      .attr('role', 'presentation')
      .attr('xmlns:xlink', 'http://www.w3.org/1999/xlink');

    const defs = svg.append('defs');

    const markerPattern = defs
      .append('pattern')
      .attr('id', 'history-marker-bg')
      .attr('patternUnits', 'objectBoundingBox')
      .attr('patternContentUnits', 'objectBoundingBox')
      .attr('width', 1)
      .attr('height', 1);

    markerPattern
      .append('image')
      .attr('href', MARKER_BG_URL)
      .attr('xlink:href', MARKER_BG_URL)
      .attr('width', 1)
      .attr('height', 1)
      .attr('preserveAspectRatio', 'xMidYMid slice');

    svg.append('g').attr('data-globe', '');
    svg.append('g').attr('data-arcs', '');
    svg.append('g').attr('data-markers', '');
    svg.append('g').attr('data-labels', '');

    this.svg = svg;
  }

  private observeVisibility(): void {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting || this.started) {
            continue;
          }

          this.started = true;
          observer.disconnect();

          const tier = getMotionTier();
          if (tier === 'full') {
            void this.runAnimation();
          } else {
            this.showFinalState({ enableDrag: tier === 'lite' });
          }
        }
      },
      { threshold: 0.35 },
    );

    observer.observe(this.container);
  }

  private observeResize(): void {
    const observer = new ResizeObserver(() => {
      this.resize();
    });

    observer.observe(this.container);
    this.resize();
  }

  private resize(): void {
    const rect = this.container.getBoundingClientRect();
    this.width = rect.width;
    this.height = rect.height;

    fitGlobeProjection(this.projection, this.width, this.height);

    this.svg.attr('viewBox', `0 0 ${this.width} ${this.height}`);
    this.render();
  }

  private pathGenerator(): ReturnType<typeof geoPath> {
    return geoPath(this.projection);
  }

  private renderArcGradients(): void {
    const defs = this.svg.select('defs');

    const gradients = defs
      .selectAll<SVGLinearGradientElement, ArcSegment>(
        'linearGradient[data-arc-gradient]',
      )
      .data(this.segments, (segment) => segment.id);

    const entered = gradients
      .enter()
      .append('linearGradient')
      .attr('data-arc-gradient', '')
      .attr('id', (segment) => arcGradientId(segment.id))
      .attr('gradientUnits', 'userSpaceOnUse');

    entered
      .append('stop')
      .attr('offset', '0%')
      .attr('stop-color', COLORS.navy)
      .attr('stop-opacity', 0);

    entered
      .append('stop')
      .attr('offset', '100%')
      .attr('stop-color', COLORS.navy);

    gradients.merge(entered).each((segment, index, nodes) => {
      const from = historyStops[index];
      const to = historyStops[index + 1];
      const start = this.projection([from.lng, from.lat]);
      const end = this.projection([to.lng, to.lat]);
      const gradient = select(nodes[index]);

      if (start && end) {
        gradient
          .attr('x1', start[0])
          .attr('y1', start[1])
          .attr('x2', end[0])
          .attr('y2', end[1]);
      }

      gradient
        .select<SVGStopElement>('stop:last-child')
        .attr('stop-opacity', segment.dimmed ? 0.35 : 1);
    });

    gradients.exit().remove();
  }

  private renderArcs(path: ReturnType<typeof geoPath>): void {
    if (this.segments.some((segment) => segment.coordinates.length > 0)) {
      this.renderArcGradients();
    }

    const arcs = this.arcsGroup
      .selectAll<SVGPathElement, ArcSegment>('path')
      .data(this.segments, (segment) => segment.id);

    arcs
      .enter()
      .append('path')
      .merge(arcs)
      .attr('fill', 'none')
      .attr('stroke', (segment) =>
        segment.coordinates.length > 0
          ? `url(#${arcGradientId(segment.id)})`
          : COLORS.navy,
      )
      .attr('stroke-linecap', 'round')
      .attr('stroke-width', (segment) => (segment.dimmed ? 1.5 : 2))
      .attr('stroke-opacity', (segment) =>
        segment.coordinates.length > 0 ? 1 : segment.dimmed ? 0.35 : 1,
      )
      .attr(
        'd',
        (segment) =>
          path({
            type: 'LineString',
            coordinates: segment.coordinates,
          }) ?? '',
      );

    arcs.exit().remove();
  }

  private renderMarkers(): void {
    const markers = this.markersGroup
      .selectAll<SVGGElement, HistoryStop>('g.marker')
      .data(historyStops, (stop) => stop.id)
      .join('g')
      .attr('class', 'marker');

    markers.each(function () {
      const group = select(this);

      if (group.select('[data-ping]').empty()) {
        group.insert('circle', ':first-child').attr('data-ping', '');
      }

      if (group.select('[data-ring]').empty()) {
        group.append('circle').attr('data-ring', '');
      }

      if (group.select('[data-dot]').empty()) {
        group.append('circle').attr('data-dot', '');
      }
    });
  }

  private renderLabels(): void {
    this.labelsGroup
      .selectAll<SVGTextElement, HistoryStop>('text')
      .data(historyStops, (stop) => stop.id)
      .join('text')
      .attr('class', 'fill-navy text-paragraph-xs font-medium')
      .attr('font-size', 12)
      .attr('font-weight', 300)
      .attr('stroke', COLORS.white)
      .attr('stroke-width', 1)
      .style('paint-order', 'stroke fill')
      .text((stop) => stop.name);
  }

  private updateMarkerAndLabelPositions(): void {
    const centerX = this.width / 2;
    const activeId = this.container.dataset.activeHalo;

    this.markersGroup
      .selectAll<SVGGElement, HistoryStop>('g.marker')
      .attr('transform', (stop) => {
        const point = this.projection([stop.lng, stop.lat]);
        if (!point) {
          return 'translate(-9999,-9999)';
        }
        return `translate(${point[0]},${point[1]})`;
      })
      .attr('opacity', (stop) => {
        const point = this.projection([stop.lng, stop.lat]);
        return point ? 1 : 0;
      })
      .each(function (stop) {
        const isHighlighted = activeId === stop.id && stop.id === FINAL_STOP_ID;
        const showPing = isHighlighted && !isMotionReduce();
        const group = select(this);

        group
          .select<SVGCircleElement>('[data-ping]')
          .attr('r', MARKER_R_ACTIVE)
          .style('transform-box', 'fill-box')
          .attr('class', showPing ? GENEVA_PING_CLASS : '')
          .attr('opacity', showPing ? null : 0);

        group
          .select<SVGCircleElement>('[data-ring]')
          .attr('r', MARKER_R_ACTIVE + MARKER_RING_WIDTH)
          .attr('fill', 'none')
          .attr('stroke', COLORS.navyRing)
          .attr('stroke-width', MARKER_RING_WIDTH)
          .attr('opacity', isHighlighted ? 1 : 0);

        group
          .select<SVGCircleElement>('[data-dot]')
          .attr('r', isHighlighted ? MARKER_R_ACTIVE : MARKER_R)
          .attr('fill', isHighlighted ? 'url(#history-marker-bg)' : COLORS.navy)
          .attr('stroke', isHighlighted ? COLORS.navyBorder : 'none')
          .attr('stroke-width', isHighlighted ? 1 : 0);
      });

    this.labelsGroup
      .selectAll<SVGTextElement, HistoryStop>('text')
      .attr('x', (stop) => {
        const point = this.projection([stop.lng, stop.lat]);
        if (!point) {
          return -9999;
        }
        return (
          point[0] + (point[0] >= centerX ? LABEL_OFFSET_X : -LABEL_OFFSET_X)
        );
      })
      .attr('y', (stop) => {
        const point = this.projection([stop.lng, stop.lat]);
        return point ? point[1] + LABEL_OFFSET_Y : -9999;
      })
      .attr('text-anchor', (stop) => {
        const point = this.projection([stop.lng, stop.lat]);
        if (!point) {
          return 'start';
        }
        return point[0] >= centerX ? 'start' : 'end';
      })
      .attr('opacity', (stop) => {
        const point = this.projection([stop.lng, stop.lat]);
        return point ? 1 : 0;
      });
  }

  private render(): void {
    const path = this.pathGenerator();
    renderOrthographicGlobe(
      this.globeGroup,
      this.projection,
      this.width,
      this.height,
    );
    this.renderArcs(path);
    this.updateMarkerAndLabelPositions();
  }

  private setActiveHalo(stop: HistoryStop): void {
    this.container.dataset.activeHalo = stop.id;
    this.render();
  }

  private clearActiveHalo(): void {
    delete this.container.dataset.activeHalo;
  }

  private animateSegment(index: number): Promise<void> {
    const from = historyStops[index];
    const to = historyStops[index + 1];
    const segment = this.segments[index];
    const { startLng, endLng } = segmentCameraBounds[index];

    return new Promise((resolve) => {
      const start = performance.now();

      const frame = (now: number): void => {
        const linear = Math.min((now - start) / SEGMENT_MS, 1);
        const progress = easeInOutCubic(linear);

        setAxisRotation(
          this.projection,
          interpolateLng(startLng, endLng, progress),
        );
        segment.coordinates = buildArcCoordinates(from, to, progress);
        segment.dimmed = false;
        this.render();

        if (linear < 1) {
          this.animationFrame = requestAnimationFrame(frame);
        } else {
          resolve();
        }
      };

      this.animationFrame = requestAnimationFrame(frame);
    });
  }

  private async runAnimation(): Promise<void> {
    this.clearActiveHalo();
    this.segments.forEach((segment) => {
      segment.coordinates = [];
      segment.dimmed = false;
    });
    setAxisRotation(this.projection, segmentCameraBounds[0].startLng);
    this.render();

    for (let index = 0; index < this.segments.length; index++) {
      await this.animateSegment(index);

      if (index < this.segments.length - 1) {
        this.segments[index].dimmed = true;
        this.segments[index].coordinates = buildArcCoordinates(
          historyStops[index],
          historyStops[index + 1],
        );
        this.render();
      }
    }

    this.setActiveHalo(historyStops[historyStops.length - 1]);
    this.enableDrag();
  }

  private showFinalState(options: { enableDrag?: boolean } = {}): void {
    const { enableDrag = true } = options;
    cancelAnimationFrame(this.animationFrame);

    this.segments.forEach((segment, index) => {
      segment.coordinates = buildArcCoordinates(
        historyStops[index],
        historyStops[index + 1],
      );
      segment.dimmed = index < this.segments.length - 1;
    });

    const geneva = historyStops[historyStops.length - 1];
    setAxisRotation(
      this.projection,
      segmentCameraBounds[segmentCameraBounds.length - 1].endLng,
    );
    this.setActiveHalo(geneva);
    if (enableDrag) {
      this.enableDrag();
    }
  }

  private enableDrag(): void {
    if (this.dragEnabled) {
      return;
    }

    this.dragEnabled = true;
    enableAxisDrag(this.svg, this.projection, () => {
      this.render();
    });
  }
}

const initialized = new WeakSet<HTMLElement>();

export const initHistoryMap = (element: HTMLElement): void => {
  if (initialized.has(element)) {
    return;
  }

  initialized.add(element);
  new HistoryMapController(element).init();
};

for (const element of document.querySelectorAll<HTMLElement>('[data-history-map]')) {
  initHistoryMap(element);
}
